/**
 * Próximas consultas do médico no Painel de Controle.
 *
 * Regra de negócio:
 * - Somente status "ativos" (agendada/solicitada/em andamento/remarcação).
 * - Instante futuro: agora comparado ao instante real da consulta.
 * - Fonte preferencial: `appointmentDate` (ISO/timestamptz) — NÃO sobrescrever com setHours(time).
 * - Fuso de negócio para fallback date+time: America/Sao_Paulo (sem DST desde 2019 → -03:00).
 * - Ordenação crescente por data/hora.
 */

import { APP_CALENDAR_TIMEZONE, formatDateKeyInTimezone } from './calendarDate';

export const UPCOMING_APPOINTMENT_STATUSES = [
  'requested',
  'RESERVADO_PENDENTE',
  'scheduled',
  'CONFIRMADO',
  'reschedule_requested',
  'in_transit',
] as const;

export type UpcomingAppointmentLike = {
  appointmentDate?: string | Date | null;
  date?: string | null;
  time?: string | null;
  status?: string | null;
};

/** Brazil (São Paulo) offset fixo para composição date+time sem ISO completo. */
const SP_OFFSET = '-03:00';

function normalizeTimeHhMm(time: string | null | undefined): string | null {
  if (!time || time === 'undefined:undefined') return null;
  const match = /^(\d{1,2}):(\d{2})/.exec(String(time).trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (
    Number.isNaN(hours) ||
    Number.isNaN(minutes) ||
    hours < 0 ||
    hours > 23 ||
    minutes < 0 ||
    minutes > 59
  ) {
    return null;
  }
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Resolve o instante absoluto da consulta.
 * Prioriza appointmentDate ISO; nunca aplica setHours sobre um ISO já correto.
 */
export function resolveAppointmentInstant(
  appointment: UpcomingAppointmentLike,
): Date | null {
  const raw = appointment.appointmentDate;
  if (raw != null && raw !== '') {
    const fromIso = raw instanceof Date ? raw : new Date(String(raw));
    if (!Number.isNaN(fromIso.getTime())) {
      // Se veio só YYYY-MM-DD dentro de appointmentDate, trata como data de calendário + time
      const asString = String(raw).trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(asString)) {
        const hhmm = normalizeTimeHhMm(appointment.time) || '00:00';
        const composed = new Date(`${asString}T${hhmm}:00${SP_OFFSET}`);
        return Number.isNaN(composed.getTime()) ? null : composed;
      }
      return fromIso;
    }
  }

  const dateOnly = appointment.date ? String(appointment.date).trim() : '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
    const hhmm = normalizeTimeHhMm(appointment.time) || '00:00';
    const composed = new Date(`${dateOnly}T${hhmm}:00${SP_OFFSET}`);
    return Number.isNaN(composed.getTime()) ? null : composed;
  }

  return null;
}

export function isUpcomingAppointmentStatus(status: string | null | undefined): boolean {
  if (!status) return false;
  return (UPCOMING_APPOINTMENT_STATUSES as readonly string[]).includes(status);
}

export function getUpcomingAppointments<T extends UpcomingAppointmentLike>(
  appointments: T[],
  now: Date = new Date(),
): T[] {
  const nowMs = now.getTime();

  return appointments
    .filter((apt) => {
      if (!isUpcomingAppointmentStatus(apt.status)) return false;
      const instant = resolveAppointmentInstant(apt);
      if (!instant) return false;
      return instant.getTime() >= nowMs;
    })
    .sort((a, b) => {
      const aMs = resolveAppointmentInstant(a)?.getTime() ?? Number.POSITIVE_INFINITY;
      const bMs = resolveAppointmentInstant(b)?.getTime() ?? Number.POSITIVE_INFINITY;
      return aMs - bMs;
    });
}

export function isAppointmentToday(
  appointment: UpcomingAppointmentLike,
  now: Date = new Date(),
  timeZone: string = APP_CALENDAR_TIMEZONE,
): boolean {
  const instant = resolveAppointmentInstant(appointment);
  if (!instant) return false;
  return formatDateKeyInTimezone(instant, timeZone) === formatDateKeyInTimezone(now, timeZone);
}
